import { RoundTitle } from "@client/components/round/RoundTitle";
import { Button } from "@client/components/utils/Button";
import ExternalLinkIcon from "@client/icons/ExternalLinkIcon";
import { DispatchContext, StateContext } from "@client/pages/round/Round";
import { trpc } from "@client/trpc";
import { getAllProposalForms } from "@engine/round/accessors";
import { proposalFormConstants } from "@lib/constants";
import { generateProposalFormId } from "@lib/utils/idGeneration";
import * as Sentry from "@sentry/react";
import { useContext, useState } from "react";
import { toast } from "react-toastify";

const proposalFormNames = {
  "SFF Rolling Application (real)":
    "https://docs.google.com/spreadsheets/d/1cVoqqyXYDmAYorQ7SAfraEOr5FDPXMcTD2BYxP5Js5Y/edit?usp=sharing",
  "SFF Rolling Application (testing)":
    "https://docs.google.com/spreadsheets/d/1y17kY9UAhJlFwJxKP32fRb2gqNcfxlbT_jwxEfkRyhE/edit?usp=sharing",
  "SFF Charity Funding Rolling Application":
    "https://docs.google.com/spreadsheets/d/1I1qewTUt_41EwZkoI25Ooxb6j9m1b75CTm7h-gBc8nc/edit?usp=sharing",
  "SFF for-profit non-dilutive grant application":
    "https://docs.google.com/spreadsheets/d/12c_AGruVvEJ44R1VW0t0O2RDRAUPggpv6EHBpfIYmkE/edit?usp=sharing",
};

export function ImportAndExportPage() {
  const { round, logStorage } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const roundId = round.id;
  const [syncing, setSyncing] = useState<string[]>([]);

  async function syncForm(formId: string) {
    if (syncing.includes(formId)) {
      return;
    }

    const currentProposals = round.proposalForms[formId]?.proposals ?? {};

    setSyncing([...syncing, formId]);
    const response = await fetch(
      `/api/syncProposals?roundId=${roundId}&proposalFormId=${formId}`,
    );
    const { actions, unmatchedApplications, missingProposals } =
      (await response.json()) as {
        actions: Array<{ type: string; [key: string]: unknown }>;
        unmatchedApplications: string[];
        missingProposals?: string[];
      };

    const syncAction = actions.find(
      (a) => a.type === "PROPOSAL_FORM_EXTERNAL_SYNC",
    ) as
      | {
          type: string;
          proposals: { [id: string]: { shortname: string } };
        }
      | undefined;

    if (syncAction) {
      const newProposals = syncAction.proposals;
      const currentIds = Object.keys(currentProposals);
      const newIds = Object.keys(newProposals);

      const added = newIds.filter((id) => !currentIds.includes(id));

      const addedNames = added.map((id) => newProposals[id].shortname);
      const removedNames = missingProposals ?? [];

      if (addedNames.length) {
        toast.success(`Added: ${addedNames.join(", ")}`);
      }
      if (removedNames.length) {
        toast.warning(
          `Previously present, now missing orgs: ${removedNames.join(", ")} (note that ${removedNames.length > 1 ? "these were" : "this was"} not removed from the round)`,
          { autoClose: false },
        );
      }
      if (!addedNames.length && !removedNames.length) {
        toast.success("Proposals synced (none added or removed)");
      }
    }

    if (unmatchedApplications.length > 0) {
      toast.error(
        `We couldn't match these applications to Google Form responses (they probably need (updated) precise timestamps): ${unmatchedApplications.join(
          ", ",
        )}`,
      );
    }

    setSyncing(syncing.filter((id) => id !== formId));
  }

  function logProposalForms() {
    const proposalForms = getAllProposalForms(round);
    for (const proposalForm of proposalForms) {
      console.log("proposalForm", proposalForm.name);
      console.log("fields: ");
      console.table(proposalForm.fields, ["type", "status", "source", "title"]);
      console.log("");
    }
  }

  function updateRoundSnapshot(fromScratch: boolean = false) {
    trpc()
      .updateRoundSnapshot.mutate({ roundId, fromScratch })
      .then(() => {
        toast.success("Snapshot updated");
      })
      .catch((e) => {
        toast.error("Error updating snapshot");
        console.error(e);
      });
  }

  function addProposalForm(name: string) {
    void dispatch({
      type: "ADD_PROPOSAL_FORM",
      proposalFormId: generateProposalFormId(),
      name,
      googleFormId: proposalFormConstants[name].googleFormId,
      spreadsheetId: proposalFormConstants[name].spreadsheetId,
      spreadsheetRange: proposalFormConstants[name].spreadsheetRange,
    });
  }

  function sentryTest() {
    Sentry.captureMessage("test");
  }

  function requestAllActions() {
    void trpc().getAggregateActions.query({ roundId, currentAggVersion: -1 });
  }

  function requestAggregate() {
    void trpc().getAggregate.query({ roundId });
  }

  const roundProposalFormsByName = Object.fromEntries(
    Object.values(round.proposalForms).map((form) => [form.name, form]),
  );

  return (
    <div className="w-full max-w-5xl">
      <RoundTitle />
      <ul className="flex flex-col gap-2">
        {Object.entries(proposalFormNames).map(([name, url]) => {
          const { action, children } =
            name in roundProposalFormsByName
              ? {
                  action: () => syncForm(roundProposalFormsByName[name].id),
                  children: "sync proposals",
                }
              : {
                  action: () => addProposalForm(name),
                  children: "add proposal form",
                };

          const isSyncing = syncing.includes(
            roundProposalFormsByName[name]?.id,
          );

          return (
            <li key={name}>
              <div className="flex items-center gap-2">
                <span>{name}</span>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLinkIcon />
                </a>
                <AdminFormButton onClick={action} disabled={isSyncing}>
                  {isSyncing ? "syncing..." : children}
                </AdminFormButton>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="h-20" />
      <h3 className="text-lg mb-2">🚧 Developer Stuff 🔨</h3>
      <ul className="flex flex-col gap-2">
        <AdminFormButton onClick={() => updateRoundSnapshot()}>
          Update Snapshot for this round
        </AdminFormButton>
        <AdminFormButton onClick={() => updateRoundSnapshot(true)}>
          Update Snapshot for this round from scratch
        </AdminFormButton>
        <AdminFormButton onClick={() => logStorage()}>
          log storage
        </AdminFormButton>
        <AdminFormButton onClick={() => logProposalForms()}>
          log proposal forms
        </AdminFormButton>
        <AdminFormButton onClick={() => sentryTest()}>
          Send test sentry event
        </AdminFormButton>
        <AdminFormButton onClick={() => requestAllActions()}>
          Request all actions
        </AdminFormButton>
        <AdminFormButton onClick={() => requestAggregate()}>
          Request aggregate
        </AdminFormButton>
      </ul>
      <div className="h-40" />
    </div>
  );
}

function AdminFormButton({
  onClick,
  children,
  disabled,
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <div>
      <Button onClick={onClick} disabled={disabled}>
        {children}
      </Button>
    </div>
  );
}
